<script setup lang="ts">
import { onScopeDispose } from 'vue'
import { onAuthContextChanged } from '@/@vxapp/Auth/authContext'
import { computed, nextTick, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import type { AnyAbility } from '@casl/ability'
import { can, canRecord, canRecordChange } from '@layouts/plugins/casl'
import { InvoiceStatus } from '@/@vxapp/@Enums/PHP/ServicesV2/Invoices/DataProviders/InvoiceStatus'
import { useInvoiceStore } from '@/@vxapp/Invoices/useInvoiceStore'
import { A, Action } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Errors } from '@/@vxapp/@Enums/Error'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { ServiceType } from '@/@vxapp/@Enums/ServiceType'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import MessagesDrawer from '@/components/Drawers/MessagesDrawer.vue'
import ServiceListToolsPanel from '@/components/ServiceLists/ServiceListToolsPanel.vue'
import InvoiceListToolbar from '@/components/ServiceLists/Invoices/InvoiceListToolbar.vue'
import InvoiceOperationsList from '@/components/ServiceLists/Invoices/InvoiceOperationsList.vue'
import {
  InvoiceListBooleanFilter,
  InvoiceListSort,
  InvoiceListToolMode,
  type InvoiceListQueryParams,
  type InvoiceListRecord,
  type InvoicePaginationMeta,
} from '@/components/ServiceLists/Invoices/invoiceListTypes'
import InvoicesListInsights from '@/components/ServiceLists/InvoicesListInsights.vue'
import InvoicesReconsiliation from '@/components/Tables/Invoices/InvoicesReconsiliation.vue'
import TableMessagesTool from '@/components/Tables/TableMessagesTool.vue'
import { ability } from '@/plugins/casl/composables/useAbility'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'

definePage({ meta: { ...PageMeta.Invoices.List.Meta, pageTitle: PageMeta.Invoices.List.Title } })

type InvoiceOperationsListApi = InstanceType<typeof InvoiceOperationsList>

const defaultQueryParams: InvoiceListQueryParams = {
  sort: InvoiceListSort.Newest,
  page: 1,
  limit: 10,
  search: undefined,
  hideInactive: true,
  filterByUnpaid: false,
  filterByPendingPayment: false,
  filterByReconsilable: false,
}

const allowedLimits = [10, 25, 50, 100]
const managedQueryKeys = Object.keys(defaultQueryParams) as Array<keyof InvoiceListQueryParams>
const log = createLogger('InvoicesListPage')
const invoiceAbility = ability as unknown as AnyAbility
const route = useRoute()
const router = useRouter()
const invoiceStore = useInvoiceStore()

const notify = useAppNotifications()
const invoiceOperationsList = useTemplateRef<InvoiceOperationsListApi>('invoiceOperationsList')
const isRouteQueryReady = shallowRef(false)
const activeTool = shallowRef<InvoiceListToolMode | null>(null)
const resultCount = shallowRef<number | null>(null)
const activeMessageInvoice = shallowRef<InvoiceListRecord | null>(null)

const toolOrigin = shallowRef<{ left: number; top: number; width: number; height: number } | null>(
  null
)

const reconciliationQuerySnapshot = shallowRef<Pick<
  InvoiceListQueryParams,
  'filterByReconsilable' | 'page'
> | null>(null)

let internallySyncedRouteQueryKey: string | null = null

const queryParams = computed<InvoiceListQueryParams>(() =>
  normalizeQueryParams(invoiceStore.queryParams as Record<string, unknown> | null)
)

const isReconcileMode = computed(() => activeTool.value === InvoiceListToolMode.Reconcile)
const isMessageMode = computed(() => activeTool.value === InvoiceListToolMode.Message)
const showSelect = computed(() => activeTool.value !== null)
const selectedItems = computed(() => invoiceStore.selectedItems as string[])
const selectedCount = computed(() => selectedItems.value.length)

const canCreate = computed(
  () =>
    invoiceAbility.can(A.CREATE, PageMeta.Invoices.Add.Meta.subject) &&
    invoiceAbility.can(A.READ, 'Invoices:load_new')
)

const canReconcile = computed(() => invoiceAbility.can(A.UPDATE, 'Invoices:Update:Guid'))

const canMessage = computed(() => invoiceAbility.can(A.UPDATE, 'Invoices:Messages:Update:Guid'))

const canUpdateStatus = computed(() =>
  invoiceAbility.can(Action.UPDATE, 'Invoices:Events:SetStatus:Update:Guid')
)

function normalizeQueryValue(value: unknown) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
    ? String(normalizedValue)
    : undefined
}

function normalizeBoolean(value: unknown, fallback: boolean) {
  if (typeof value === 'boolean') return value

  const normalizedValue = normalizeQueryValue(value)

  if (normalizedValue === 'true') return true
  if (normalizedValue === 'false') return false

  return fallback
}

function normalizePositiveInteger(value: unknown, fallback: number) {
  const parsedValue = Number.parseInt(normalizeQueryValue(value) ?? '', 10)

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeLimit(value: unknown) {
  const normalizedLimit = normalizePositiveInteger(value, defaultQueryParams.limit)

  return allowedLimits.includes(normalizedLimit) ? normalizedLimit : defaultQueryParams.limit
}

function normalizeQueryParams(source: Record<string, unknown> | null): InvoiceListQueryParams {
  const search = normalizeQueryValue(source?.search)?.trim()
  const sort = normalizeQueryValue(source?.sort)

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source?.page, defaultQueryParams.page),
    limit: normalizeLimit(source?.limit),
    sort: sort || defaultQueryParams.sort,
    search: search || undefined,
    hideInactive: normalizeBoolean(source?.hideInactive, defaultQueryParams.hideInactive),
    filterByUnpaid: normalizeBoolean(source?.filterByUnpaid, defaultQueryParams.filterByUnpaid),
    filterByPendingPayment: normalizeBoolean(
      source?.filterByPendingPayment,
      defaultQueryParams.filterByPendingPayment
    ),
    filterByReconsilable: normalizeBoolean(
      source?.filterByReconsilable,
      defaultQueryParams.filterByReconsilable
    ),
  }
}

function queryParamsMatch(left: InvoiceListQueryParams, right: InvoiceListQueryParams) {
  return managedQueryKeys.every(key => left[key] === right[key])
}

function applyRouteQuery() {
  const currentQueryParams = normalizeQueryParams(
    invoiceStore.queryParams as Record<string, unknown> | null
  )

  const routeQueryParams = normalizeQueryParams(route.query)

  if (queryParamsMatch(currentQueryParams, routeQueryParams)) return false

  Object.assign(invoiceStore.queryParams, currentQueryParams, routeQueryParams)

  return true
}

function buildRouteQuery(normalizedQueryParams: InvoiceListQueryParams = queryParams.value) {
  const nextQuery = { ...route.query }

  for (const key of managedQueryKeys) delete nextQuery[key]

  for (const key of managedQueryKeys) {
    const value = normalizedQueryParams[key]

    if (value !== defaultQueryParams[key] && value !== undefined) nextQuery[key] = String(value)
  }

  return nextQuery
}

async function syncRouteQuery(normalizedQueryParams: InvoiceListQueryParams = queryParams.value) {
  const nextQuery = buildRouteQuery(normalizedQueryParams)
  const nextQueryKey = JSON.stringify(nextQuery)

  if (JSON.stringify(route.query) === nextQueryKey) return

  internallySyncedRouteQueryKey = nextQueryKey
  await router.replace({ query: nextQuery })
}

async function refreshInvoices(requestedQuery?: InvoiceListQueryParams) {
  await invoiceOperationsList.value?.fetchInvoices(requestedQuery)
}

async function handleRefreshInvoices() {
  await refreshInvoices()
}

async function updateQueryParams(patch: Partial<InvoiceListQueryParams>) {
  const nextQueryParams = normalizeQueryParams({ ...queryParams.value, ...patch })

  Object.assign(invoiceStore.queryParams, nextQueryParams)

  if (isRouteQueryReady.value) await syncRouteQuery(nextQueryParams)

  await nextTick()
  await refreshInvoices(nextQueryParams)
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleFilterChange(key: InvoiceListBooleanFilter, value: boolean) {
  const patch: Partial<InvoiceListQueryParams> = { page: 1 }

  if (key === InvoiceListBooleanFilter.HideInactive) patch.hideInactive = value
  if (key === InvoiceListBooleanFilter.Unpaid) {
    patch.filterByUnpaid = value
    if (value) patch.filterByPendingPayment = false
  }
  if (key === InvoiceListBooleanFilter.PendingPayment) {
    patch.filterByPendingPayment = value
    if (value) patch.filterByUnpaid = false
  }

  await updateQueryParams(patch)
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({ limit: normalizeLimit(limit), page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

async function handleClearFilters() {
  const nextQueryParams = normalizeQueryParams({ ...defaultQueryParams })

  Object.assign(invoiceStore.queryParams, nextQueryParams)

  if (isRouteQueryReady.value) await syncRouteQuery(nextQueryParams)

  await nextTick()
  await refreshInvoices(nextQueryParams)
}

function handleListLoaded(pagination: InvoicePaginationMeta) {
  resultCount.value = pagination.entriesFound
}

function resetSelection() {
  invoiceStore.resetSelectedItems()
}

function getToolOrigin(event?: MouseEvent) {
  if (!(event?.currentTarget instanceof HTMLElement)) return null

  const { left, top, width, height } = event.currentTarget.getBoundingClientRect()

  return { left, top, width, height }
}

async function activateTool(tool: InvoiceListToolMode, event?: MouseEvent) {
  const nextTool = activeTool.value === tool ? null : tool

  const isEnteringReconciliation =
    nextTool === InvoiceListToolMode.Reconcile && activeTool.value !== InvoiceListToolMode.Reconcile

  const isLeavingReconciliation =
    activeTool.value === InvoiceListToolMode.Reconcile && nextTool !== InvoiceListToolMode.Reconcile

  activeTool.value = nextTool
  toolOrigin.value = nextTool ? getToolOrigin(event) : null
  resetSelection()

  if (isEnteringReconciliation) {
    if (!queryParams.value.filterByReconsilable) {
      reconciliationQuerySnapshot.value = {
        filterByReconsilable: false,
        page: queryParams.value.page,
      }
      await updateQueryParams({ filterByReconsilable: true, page: 1 })
    }
  }

  if (isLeavingReconciliation && reconciliationQuerySnapshot.value) {
    const snapshot = reconciliationQuerySnapshot.value

    reconciliationQuerySnapshot.value = null

    if (
      queryParams.value.filterByReconsilable !== snapshot.filterByReconsilable ||
      queryParams.value.page !== snapshot.page
    ) {
      await updateQueryParams(snapshot)
    }
  }
}

async function handleReconcileMode(event?: MouseEvent) {
  await activateTool(InvoiceListToolMode.Reconcile, event)
}

async function handleMessageMode(event?: MouseEvent) {
  await activateTool(InvoiceListToolMode.Message, event)
}

async function handleCloseActiveTool() {
  if (activeTool.value) await activateTool(activeTool.value)
}

function handleSelectionChange(guid: string, selected: boolean) {
  const nextSelection = new Set(selectedItems.value)

  if (selected) nextSelection.add(guid)
  else nextSelection.delete(guid)

  selectedItems.value.splice(0, selectedItems.value.length, ...nextSelection)
}

function handleNewInvoiceNavigation() {
  if (!canCreate.value) return
  router.push({ name: PageMeta.Invoices.Add.Name as 'apps-invoice-add' })
}

function assertCanUpdateStatus(invoice: InvoiceListRecord, status: InvoiceStatus) {
  if (
    canRecordChange(
      'update',
      'Invoices:Events:SetStatus:Update:Guid',
      invoice,
      { ...invoice, invoiceStatus: status },
      'invoiceStatus'
    )
  )
    return true

  notify.error({
    error: Errors.Auth.ACTION_FORBIDDEN,
    pageSubject: 'Invoices:Events:SetStatus:Update:Guid',
  })

  return false
}

async function handleMarkPaid(invoice: InvoiceListRecord) {
  if (!assertCanUpdateStatus(invoice, InvoiceStatus.PAID)) return

  try {
    const response = await invoiceStore.markPaid(invoice.invoiceGUID)

    if (response?.responseCode === ResponseCode.OK) notify.success(response.message)
    else notify.error(response?.message || 'Error setting invoice status')

    await refreshInvoices()
  } catch (error) {
    log.error('Mark invoice paid failed', error)
    notify.error('Invoice could not be marked paid')
  }
}

async function handleMarkInactive(invoice: InvoiceListRecord) {
  if (!assertCanUpdateStatus(invoice, InvoiceStatus.INACTIVE)) return

  try {
    const response = await invoiceStore.markInactive(invoice.invoiceGUID)

    if (response?.responseCode === ResponseCode.OK) notify.success(response.message)
    else notify.error(response?.message || 'Error setting invoice status')

    await refreshInvoices()
  } catch (error) {
    log.error('Mark invoice inactive failed', error)
    notify.error('Invoice could not be marked inactive')
  }
}

function handleMessageInvoice(invoice: InvoiceListRecord) {
  if (
    !can('read', 'Messages:Templates:Preview:All') ||
    !canRecord('read', 'Invoices:Messages:load_new:Guid:Guid', invoice)
  )
    return

  activeMessageInvoice.value = invoice
  invoiceStore.setInvoice(invoice)
  invoiceStore.isMessagesDrawerOpen = true
}

async function handleTableMessagesToolReload() {
  resetSelection()
  await refreshInvoices()
}

onMounted(async () => {
  invoiceStore.resetDataTableState()
  invoiceStore.setDataTableState({
    paginationMeta: invoiceStore.paginationMeta,
    queryParams: defaultQueryParams,
    staticSearchParams: invoiceStore.staticSearchParams,
  })
  applyRouteQuery()
  isRouteQueryReady.value = true
  await nextTick()
  await refreshInvoices()
})

watch(
  () => route.query,
  async () => {
    const routeQueryKey = JSON.stringify(route.query)
    const wasSyncedInternally = internallySyncedRouteQueryKey === routeQueryKey
    const requestedQuery = normalizeQueryParams(route.query)

    if (wasSyncedInternally) internallySyncedRouteQueryKey = null

    if (!isRouteQueryReady.value || wasSyncedInternally || !applyRouteQuery()) return

    await nextTick()
    await refreshInvoices(requestedQuery)
  },
  { deep: true }
)
onScopeDispose(
  onAuthContextChanged(() => {
    activeTool.value = null
    resultCount.value = 0
    activeMessageInvoice.value = null
    reconciliationQuerySnapshot.value = null
  })
)
</script>

<template>
  <section
    class="invoices-workspace"
    :class="{ 'invoices-workspace--tool-active': showSelect }"
    data-testid="invoices-workspace"
  >
    <MessagesDrawer
      v-if="activeMessageInvoice"
      :key="activeMessageInvoice.invoiceGUID"
      v-model:is-drawer-open="invoiceStore.isMessagesDrawerOpen"
      :service-g-u-i-d="activeMessageInvoice.invoiceGUID"
      service-type="Invoices"
    />

    <InvoicesListInsights>
      <template #summary>
        <div class="invoices-workspace__summary">
          <div class="invoices-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
          <div class="invoices-workspace__metric">
            <span>Selected</span>
            <strong>{{ selectedCount }}</strong>
          </div>
        </div>
      </template>
    </InvoicesListInsights>

    <InvoiceListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="invoiceStore.isLoading"
      @search="handleSearch"
      @filter-change="handleFilterChange"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClearFilters"
      @refresh="handleRefreshInvoices"
    />

    <InvoiceOperationsList
      ref="invoiceOperationsList"
      :query-params="queryParams"
      :show-select="showSelect"
      :selected-invoices="selectedItems"
      :can-message="invoiceAbility.can(A.READ, 'Invoices:Messages:load_new:Guid:Guid')"
      :can-update-status="canUpdateStatus"
      @page-change="handlePageChange"
      @selection-change="handleSelectionChange"
      @mark-paid="handleMarkPaid"
      @mark-inactive="handleMarkInactive"
      @message="handleMessageInvoice"
      @loaded="handleListLoaded"
    >
      <template #actions>
        <div
          class="invoices-workspace__actions"
          data-testid="invoice-workspace-actions"
        >
          <VBtn
            v-if="canCreate"
            data-testid="new-invoice-action"
            color="primary"
            prepend-icon="tabler-plus"
            @click="handleNewInvoiceNavigation"
          >
            New invoice
          </VBtn>
          <VBtn
            v-if="canReconcile"
            data-testid="reconcile-invoices-action"
            :color="isReconcileMode ? 'primary' : 'secondary'"
            :prepend-icon="isReconcileMode ? 'tabler-x' : 'tabler-transaction-dollar'"
            :variant="isReconcileMode ? 'flat' : 'tonal'"
            @click="handleReconcileMode"
          >
            {{ isReconcileMode ? 'Stop reconciling' : 'Reconcile invoices' }}
          </VBtn>
          <VBtn
            v-if="canMessage"
            data-testid="message-invoices-action"
            :color="isMessageMode ? 'primary' : 'secondary'"
            :prepend-icon="isMessageMode ? 'tabler-x' : 'tabler-mail-forward'"
            :variant="isMessageMode ? 'flat' : 'tonal'"
            @click="handleMessageMode"
          >
            {{ isMessageMode ? 'Stop messaging' : 'Message invoices' }}
          </VBtn>
        </div>
      </template>
    </InvoiceOperationsList>

    <ServiceListToolsPanel
      v-if="showSelect"
      :key="activeTool ?? undefined"
      data-testid="invoice-tools-panel"
      :title="isReconcileMode ? 'Reconcile invoices' : 'Message invoices'"
      :selected-count="selectedCount"
      :origin="toolOrigin ?? undefined"
      tool-label="invoice"
      @close="handleCloseActiveTool"
    >
      <InvoicesReconsiliation
        v-if="isReconcileMode"
        compact
        :selected-invoices="invoiceStore.selectedItems"
      />
      <TableMessagesTool
        v-else-if="isMessageMode"
        compact
        :title="PageMeta.Invoices.TableMessagesTool.Title"
        :selected-items="invoiceStore.selectedItems"
        :selected-records="invoiceStore.invoices ?? []"
        :service-type="ServiceType.Invoices"
        @cancel="handleCloseActiveTool"
        @reload-table="handleTableMessagesToolReload"
      />
    </ServiceListToolsPanel>
  </section>
</template>

<style scoped lang="scss">
.invoices-workspace {
  display: grid;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.invoices-workspace--tool-active {
  padding-block-end: min(60dvh, 38rem);
}

.invoices-workspace__summary {
  display: flex;
  flex: 0 0 auto;
  gap: 0.75rem;
}

.invoices-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.invoices-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  font-weight: 510;
  text-transform: uppercase;
}

.invoices-workspace__metric strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1.25rem;
  font-weight: 590;
  letter-spacing: -0.03em;
}

.invoices-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.invoices-workspace__actions :deep(.v-btn) {
  min-block-size: 2.75rem;
  padding-inline: 1.1rem;
}

@media (max-width: 959px) {
  .invoices-workspace__summary {
    align-self: stretch;
  }

  .invoices-workspace__metric {
    flex: 1;
  }
}

@media (max-width: 599px) {
  .invoices-workspace {
    gap: 1rem;
  }

  .invoices-workspace__summary,
  .invoices-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invoices-workspace__actions {
    inline-size: 100%;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 11rem), 1fr));
  }

  .invoices-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }

  .invoices-workspace__actions :deep(.v-btn:first-child) {
    grid-column: 1 / -1;
  }

  .invoices-workspace__metric {
    min-inline-size: 0;
    padding: 0.65rem 0.75rem;
  }
}
</style>
